import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useCallback, useContext, useState, useMemo, useEffect, useReducer, useRef } from 'react';
import PropTypes from 'prop-types';
var Context = /*#__PURE__*/React.createContext();
export function useProductFilters() {
  return useContext(Context);
}

var expandedCollapsibleFiltersReducer = (prevState, action) => {
  var state = _objectSpread({}, prevState);

  if (action.type === 'reset') {
    return {};
  } else if (action.type === 'open_all' && action.filterFields) {
    action.filterFields.forEach(field => state[field] = true);
  } else {
    state[action.filterField] = !state[action.filterField];
  }

  return state;
};

export default function ProductFilterContext(_ref) {
  var appliedFilters = _ref.appliedFilters,
      children = _ref.children,
      filterSettingsFromProps = _ref.filterSettings,
      onApply = _ref.onApply,
      onChange = _ref.onChange,
      onChangeFilterSettings = _ref.onChangeFilterSettings;
  var appliedFiltersRef = useRef(appliedFilters);
  var filterSettingsRef = useRef(filterSettingsFromProps); // If we get a new `appliedFilters` prop, we should temporarily ignore
  // whatever value is in local state until we can sync it back up with
  // `appliedFilters`. Any changes will be lost (they may no longer be
  // relevant to the new set of filter fields anyway).

  var areFiltersChanging = appliedFilters !== appliedFiltersRef.current;
  var areFilterSettingsChanging = filterSettingsFromProps !== filterSettingsRef.current;

  var _useState = useState(appliedFilters),
      _useState2 = _slicedToArray(_useState, 2),
      selectedFiltersFromState = _useState2[0],
      setSelectedFilters = _useState2[1];

  var selectedFilters = areFiltersChanging ? appliedFilters : selectedFiltersFromState;

  var _useState3 = useState(filterSettingsFromProps),
      _useState4 = _slicedToArray(_useState3, 2),
      filterSettingsFromState = _useState4[0],
      setFilterSettings = _useState4[1];

  var filterSettings = areFilterSettingsChanging ? filterSettingsFromProps : filterSettingsFromState; // TODO? Alternatively this could go into ProductBrowserSidebar state

  var _useReducer = useReducer(expandedCollapsibleFiltersReducer, {}),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      expandedCollapsibleFilters = _useReducer2[0],
      toggleCollapsibleFilterIsOpen = _useReducer2[1]; // Sync internal state when `appliedFilters` changes.


  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
    setSelectedFilters(appliedFilters);
  }, [appliedFilters]); // Sync internal state when `filterSettings` changes.

  useEffect(() => {
    filterSettingsRef.current = filterSettingsFromProps;
    setFilterSettings(filterSettingsFromProps);
  }, [filterSettingsFromProps]);
  var isDirty = selectedFilters !== appliedFilters;
  var updateSelectedFilters = useCallback(newFilters => {
    setSelectedFilters(newFilters);

    if (onChange) {
      onChange(newFilters);
    }
  }, [onChange]);
  var value = useMemo(() => {
    var applySelectedFilters = () => {
      if (onApply) {
        onApply({
          newFilters: selectedFilters,
          newFilterSettings: filterSettings
        });
      }
    };

    var applyFilters = _ref2 => {
      var newFilters = _ref2.newFilters,
          newFilterSettings = _ref2.newFilterSettings;

      if (onApply) {
        onApply({
          newFilters,
          newFilterSettings
        });
      }
    };

    var updateFilterSettings = newFilterSettings => {
      setFilterSettings(newFilterSettings);

      if (onChangeFilterSettings) {
        onChangeFilterSettings(newFilterSettings);
      }
    };

    return {
      appliedFilters,
      applyFilters,
      applySelectedFilters,
      filterSettings,
      initialFilterSettings: filterSettingsFromProps,
      selectedFilters,
      setSelectedFilters: updateSelectedFilters,
      setFilterSettings: updateFilterSettings,
      expandedCollapsibleFilters,
      toggleCollapsibleFilterIsOpen,
      isDirty
    };
  }, [appliedFilters, isDirty, filterSettings, filterSettingsFromProps, updateSelectedFilters, selectedFilters, onApply, onChangeFilterSettings, expandedCollapsibleFilters, toggleCollapsibleFilterIsOpen]);
  return /*#__PURE__*/React.createElement(Context.Provider, {
    value: value
  }, children);
}
ProductFilterContext.propTypes = {
  appliedFilters: PropTypes.object,
  children: PropTypes.node,
  filterSettings: PropTypes.arrayOf(PropTypes.object),
  onApply: PropTypes.func,
  onChange: PropTypes.func,
  onChangeFilterSettings: PropTypes.func
};